import type { ClientValueHistory } from "@repo/shared/types";
import { TrendingUp } from "lucide-react";
import { useMemo } from "react";
import { ValueHistoryChart } from "@/components/charts/value-history-chart";
import { Card, CardContent, CardHeader } from "@/components/ui/card";
import { useBrandColor } from "@/lib/context/brand-color-context";
import { useUserLanguage } from "@/lib/i18n/context";
import { useT } from "@/lib/i18n/use-t";
import { useRegionalFormat } from "@/lib/regional-format/context";
import { T } from "./total-value-card-view.i18n";

type TotalValueCardViewProps = {
  /** A history with at least one point. */
  history: ClientValueHistory;
};

/** The "Total value" card for a history that has points (QL-356). */
export function TotalValueCardView({ history }: TotalValueCardViewProps) {
  const { userLanguage } = useUserLanguage();
  const { formatNumber, formatDate } = useRegionalFormat();
  const brandColor = useBrandColor();
  const t = useT(T);

  const formatAmount = (value: number) =>
    `${history.currency} ${formatNumber(value, { maximumFractionDigits: 0 })}`;

  const data = useMemo(
    () =>
      history.points.map((p) => ({
        ...p,
        dateLabel: formatDate(p.date),
        valueLabel: `${history.currency} ${formatNumber(p.value, { maximumFractionDigits: 0 })}`,
      })),
    [history, formatDate, formatNumber],
  );

  const partial = history.includedAccountCount < history.totalAccountCount;
  const subtitle = partial
    ? `${t.someAccounts(history.currency)} · ${t.basedOn(
        history.includedAccountCount,
        history.totalAccountCount,
      )}`
    : t.allAccounts(history.currency);

  return (
    <Card>
      <CardHeader className="flex flex-row items-start justify-between gap-4 space-y-0">
        <div className="space-y-1">
          <h3 className="flex items-center gap-2 text-lg font-semibold leading-none">
            <TrendingUp className="size-5" aria-hidden />
            {t.title}
          </h3>
          <p className="text-sm text-muted-foreground">{subtitle}</p>
        </div>
        <div className="text-right">
          <div className="text-3xl font-bold tracking-tight tabular-nums">
            {formatAmount(history.latest!.value)}
          </div>
          <div className="text-sm text-muted-foreground">
            {t.asOf(formatDate(history.latest!.date))}
          </div>
        </div>
      </CardHeader>
      <CardContent>
        <ValueHistoryChart
          data={data}
          height={260}
          primaryColor={brandColor ?? undefined}
          lang={userLanguage}
        />
      </CardContent>
    </Card>
  );
}
